import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  BellRing,
  CheckCircle2,
  Clock3,
  Loader2,
  Mail,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/contexts/auth-context";
import { audioNotifier } from "@/lib/audio-notifier";
import type {
  ShareEmailDelivery,
  ShareEmailDeliveryAction,
} from "@shared/share-email-delivery";

/**
 * Durable share deliveries are deliberately monitored outside of the
 * composer. A queued delivery must remain visible when the user navigates to
 * another project (or closes the composer), and terminal states must remain
 * visible until the sender acknowledges them.
 */
export function ShareEmailDeliveryMonitor() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const queryClient = useQueryClient();
  const [warningQueue, setWarningQueue] = useState<string[]>([]);
  const [warningId, setWarningId] = useState<string | null>(null);
  const [snoozedUntil, setSnoozedUntil] = useState<Record<string, number>>({});
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const playedAlertKeysRef = useRef(new Set<string>());
  const refreshedHistoryRef = useRef(new Set<string>());
  const [warningClock, setWarningClock] = useState(() => Date.now());
  const deliveryQueryKey = useMemo(
    () => ["/api/emails/share-deliveries", user?.id] as const,
    [user?.id],
  );

  const { data, isError } = useQuery<{ deliveries: ShareEmailDelivery[] }>({
    queryKey: deliveryQueryKey,
    enabled: !isLoading && isAuthenticated,
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/emails/share-deliveries");
      return response.json();
    },
    // Active work needs a responsive monitor. Once all work is idle, a
    // slower refresh still keeps terminal notices current without polling
    // aggressively forever. Focus refetches immediately in either case.
    refetchInterval: (query) => {
      const deliveries = (query.state.data as { deliveries?: ShareEmailDelivery[] } | undefined)?.deliveries || [];
      return deliveries.some((delivery) =>
        delivery.state === "waiting" ||
        delivery.state === "needs_confirmation" ||
        delivery.state === "sending" ||
        (delivery.state === "sent" && !delivery.recordedAt)
      ) ? 2_000 : 15_000;
    },
    refetchOnWindowFocus: true,
    staleTime: 1_000,
  });

  const deliveries = data?.deliveries || [];
  const deliveryById = useMemo(
    () => new Map(deliveries.map((delivery) => [delivery.id, delivery])),
    [deliveries],
  );

  // Queue acceptance is not history. Refresh only once SMTP confirmation has
  // produced a sent delivery, including confirmations first seen after reload.
  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;
    for (const delivery of deliveries) {
      if (delivery.state !== "sent" || !delivery.recordedAt) continue;
      const key = `${user.id}:${delivery.id}:${delivery.recordedAt}`;
      if (refreshedHistoryRef.current.has(key)) continue;
      refreshedHistoryRef.current.add(key);
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/tree/snapshot"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/years"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/projects", delivery.projectId] }),
        queryClient.invalidateQueries({ queryKey: ["/api/projects", delivery.projectId, "pulses", "paginated"] }),
      ]);
    }
  }, [deliveries, isLoading, isAuthenticated, user?.id, queryClient]);

  // React Query may structurally share an unchanged response. Keep the
  // four-minute threshold accurate even when the server payload itself has
  // not changed between polls.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    const timer = window.setInterval(() => setWarningClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [isAuthenticated, isLoading]);

  const actionMutation = useMutation({
    mutationFn: async ({
      id,
      action,
      version,
    }: {
      id: string;
      action: ShareEmailDeliveryAction;
      version?: string;
    }) => {
      const response = await apiRequest("POST", `/api/emails/share-deliveries/${encodeURIComponent(id)}/action`, {
        action,
        ...(version ? { version } : {}),
      });
      return (await response.json()) as { delivery: ShareEmailDelivery };
    },
    onSuccess: ({ delivery }, variables) => {
      setActionError(null);
      queryClient.setQueryData<{ deliveries: ShareEmailDelivery[] }>(
        deliveryQueryKey,
        (current) => ({
          deliveries: variables.action === "acknowledge"
            ? (current?.deliveries || []).filter((item) => item.id !== delivery.id)
            : (current?.deliveries || []).map((item) => item.id === delivery.id ? delivery : item),
        }),
      );
      if (delivery.state !== "needs_confirmation") {
        setWarningQueue((current) => current.filter((id) => id !== delivery.id));
        setWarningId((current) => current === delivery.id ? null : current);
      }
      if (variables.action !== "acknowledge") {
        void queryClient.invalidateQueries({ queryKey: deliveryQueryKey });
      }
    },
    onError: (error: any) => {
      setActionError(error?.message || "The delivery action could not be completed. Refresh to check the current status.");
      void queryClient.invalidateQueries({ queryKey: deliveryQueryKey });
    },
  });

  // Query keys isolate cache entries, while this clears any foreground
  // warning/snooze state that belonged to the previous account immediately
  // during an auth switch.
  useEffect(() => {
    setWarningQueue([]);
    setWarningId(null);
    setSnoozedUntil({});
    setActionError(null);
    setAudioBlocked(false);
  }, [user?.id]);

  // The backend changes alertKey for each new warning. Persisting the key
  // prevents a sound on every 2-second refetch while still alerting for a
  // genuinely new warning after a reload.
  useEffect(() => {
    if (!user || deliveries.length === 0) return;
    const storage = typeof window !== "undefined" ? window.localStorage : null;
    const userKey = String(user.id);
    for (const delivery of deliveries) {
      if (!delivery.alertKey || delivery.alert === null) continue;
      const storageKey = `share-email-delivery-alert:${userKey}:${delivery.id}:${delivery.alertKey}`;
      let alreadyPlayed = playedAlertKeysRef.current.has(storageKey);
      try {
        if (!alreadyPlayed) alreadyPlayed = storage?.getItem(storageKey) === "1";
      } catch {
        // Private browsing may deny storage. The in-memory key below still
        // keeps this session from replaying the same alert.
      }
      playedAlertKeysRef.current.add(storageKey);
      if (!alreadyPlayed) {
        try { storage?.setItem(storageKey, "1"); } catch { /* best effort */ }
      }
      if (!alreadyPlayed) {
        audioNotifier.playByType("email");
        if (!audioNotifier.isReady()) setAudioBlocked(true);
      }
    }
  }, [deliveries, user]);

  // Queue all warnings instead of replacing one warning with another job.
  // Four minutes is intentionally calculated from the durable creation time,
  // so navigating away cannot postpone the warning.
  useEffect(() => {
    const now = warningClock;
    const eligible = deliveries
      .filter((delivery) => {
        const active = delivery.state === "waiting" ||
          delivery.state === "needs_confirmation" ||
          delivery.state === "sending";
        if (!active) return false;
        const oldEnough = now - new Date(delivery.createdAt).getTime() >= 4 * 60 * 1000;
        const changedFiles = delivery.alert === "files_changed" || delivery.state === "needs_confirmation";
        const notSnoozed = (snoozedUntil[delivery.id] || 0) <= now;
        return (oldEnough || changedFiles) && notSnoozed;
      })
      .map((delivery) => delivery.id);
    setWarningQueue((current) => {
      const stillKnown = current.filter((id) => eligible.includes(id));
      const additions = eligible.filter((id) => !stillKnown.includes(id));
      const next = [...stillKnown, ...additions];
      return next.length === current.length && next.every((id, index) => id === current[index])
        ? current
        : next;
    });
  }, [deliveries, snoozedUntil, warningClock]);

  useEffect(() => {
    if (warningId && warningQueue.includes(warningId)) return;
    setWarningId(warningQueue[0] || null);
  }, [warningId, warningQueue]);

  const activeWarning = warningId ? deliveryById.get(warningId) : undefined;
  const smtpSubmissionInProgress = activeWarning?.state === "sending";

  const performAction = useCallback((delivery: ShareEmailDelivery, action: ShareEmailDeliveryAction) => {
    actionMutation.mutate({
      id: delivery.id,
      action,
      // Confirm is the only action that accepts a readiness fingerprint.
      // Never expose or send message content from this DTO.
      version: action === "confirm" ? delivery.readiness.version : undefined,
    });
  }, [actionMutation]);

  const dismissWarningFor = (id: string) => {
    setWarningQueue((current) => current.filter((item) => item !== id));
    setWarningId(null);
  };

  const snoozeWarning = (delivery: ShareEmailDelivery) => {
    setSnoozedUntil((current) => ({
      ...current,
      [delivery.id]: Date.now() + 5 * 60 * 1000,
    }));
    dismissWarningFor(delivery.id);
    performAction(delivery, "wait");
  };

  if (isLoading || !isAuthenticated) return null;

  const pending = deliveries.filter((delivery) =>
    delivery.state !== "sent" &&
    delivery.state !== "failed" &&
    delivery.state !== "uncertain" &&
    delivery.state !== "expired" &&
    delivery.state !== "canceled"
  );
  const terminal = deliveries.filter((delivery) =>
    delivery.state === "sent" ||
    delivery.state === "failed" ||
    delivery.state === "uncertain" ||
    delivery.state === "expired" ||
    delivery.state === "canceled"
  );

  return (
    <>
      {(pending.length > 0 || terminal.length > 0 || isError || actionError) && (
        <aside
          className="fixed bottom-4 right-4 z-[80] w-[min(420px,calc(100vw-2rem))] space-y-2"
          aria-label="Share email delivery monitor"
          data-testid="share-delivery-monitor"
        >
          {isError && (
            <div className="rounded-lg border border-rose-700 bg-rose-950/95 p-3 text-sm text-rose-100 shadow-xl" role="alert">
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4" /> Could not refresh share email deliveries
              </div>
              <p className="mt-1 text-xs text-rose-200">Existing jobs were not forgotten. Please refresh to check their current status.</p>
            </div>
          )}
          {actionError && (
            <div className="rounded-lg border border-rose-700 bg-rose-950/95 p-3 text-sm text-rose-100 shadow-xl" role="alert" data-testid="share-delivery-action-error">
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4" /> Delivery action needs attention
              </div>
              <p className="mt-1 text-xs text-rose-200">{actionError}</p>
            </div>
          )}

          {pending.map((delivery) => (
            <DeliveryCard
              key={delivery.id}
              delivery={delivery}
              busy={actionMutation.isPending}
              onAction={performAction}
            />
          ))}

          {terminal.map((delivery) => (
            <TerminalCard
              key={delivery.id}
              delivery={delivery}
              busy={actionMutation.isPending}
              onAcknowledge={() => performAction(delivery, "acknowledge")}
            />
          ))}
        </aside>
      )}

      {activeWarning && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-delivery-warning-title"
          data-testid="share-delivery-warning"
        >
          <div className="w-full max-w-xl rounded-xl border border-amber-600 bg-slate-950 p-6 text-white shadow-2xl">
            <div className="flex items-start gap-3">
              <BellRing className="mt-0.5 h-6 w-6 shrink-0 text-amber-400" />
              <div className="min-w-0">
                <h2 id="share-delivery-warning-title" className="text-lg font-semibold">
                  {activeWarning.alert === "files_changed" || activeWarning.state === "needs_confirmation"
                    ? "Files changed while waiting"
                    : "Share email is still waiting"}
                </h2>
                <p className="mt-2 text-sm text-slate-300">
                  Automatic sending is still active for <strong>{activeWarning.previewTitle}</strong>.
                  {activeWarning.alert === "files_changed" || activeWarning.state === "needs_confirmation"
                    ? " The current file list is different from the one you approved."
                     : smtpSubmissionInProgress
                       ? " SMTP submission is in progress. Wait for the confirmed result; sending another copy or canceling is not safe."
                       : " You can keep waiting, send it now, or cancel it."}
                </p>
              </div>
            </div>

            {(activeWarning.alert === "files_changed" || activeWarning.state === "needs_confirmation") && (
              <div className="mt-4 rounded-md border border-amber-800/80 bg-amber-950/40 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-300">Current file list</p>
                <ul className="mt-2 space-y-1 text-sm text-slate-200" data-testid="share-delivery-current-files">
                  {activeWarning.readiness.files.map((file) => (
                    <li key={file.id} className="flex items-center justify-between gap-3">
                      <span className="truncate">{file.fileName}</span>
                      <span className={file.ready ? "text-emerald-300" : "text-amber-300"}>{file.detail}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-slate-400">Confirming approves this current version only.</p>
              </div>
            )}

            {audioBlocked && (
              <p className="mt-4 rounded-md border border-slate-700 bg-slate-900 p-3 text-xs text-slate-300" data-testid="share-delivery-audio-blocked">
                Your browser blocked the notification sound. This warning stays open until you choose an action.
              </p>
            )}
            {!audioBlocked && (
              <p className="mt-4 text-xs text-slate-400" data-testid="share-delivery-audio-cue">
                A notification sound is played once for this warning. If you do not hear it, your browser may have blocked audio; the warning remains visible.
              </p>
            )}

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              {(activeWarning.alert === "files_changed" || activeWarning.state === "needs_confirmation") && (
                <Button
                  type="button"
                  disabled={actionMutation.isPending}
                  onClick={() => performAction(activeWarning, "confirm")}
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="share-delivery-confirm-files"
                >
                  Confirm current file list
                </Button>
              )}
              <Button
                type="button"
                disabled={actionMutation.isPending || smtpSubmissionInProgress}
                onClick={() => performAction(activeWarning, "force")}
                className="bg-amber-600 text-white hover:bg-amber-500"
                data-testid="share-delivery-force"
                title={smtpSubmissionInProgress ? "SMTP submission is in progress; sending again could duplicate the email." : undefined}
              >
                Send NOW!
              </Button>
              {(smtpSubmissionInProgress || (activeWarning.alert !== "files_changed" && activeWarning.state !== "needs_confirmation")) && (
                <Button
                  type="button"
                  disabled={actionMutation.isPending}
                  onClick={() => snoozeWarning(activeWarning)}
                  variant="outline"
                  className="border-slate-600 text-slate-200"
                  data-testid="share-delivery-wait"
                >
                  Wait another 5 minutes
                </Button>
              )}
              <Button
                type="button"
                disabled={actionMutation.isPending || smtpSubmissionInProgress}
                onClick={() => {
                  dismissWarningFor(activeWarning.id);
                  performAction(activeWarning, "cancel");
                }}
                variant="ghost"
                className="text-rose-300 hover:bg-rose-950/50 hover:text-rose-200"
                data-testid="share-delivery-cancel"
                title={smtpSubmissionInProgress ? "SMTP submission is in progress; canceling could leave delivery status unknown." : undefined}
              >
                Cancel Sending
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DeliveryCard({
  delivery,
  busy,
  onAction,
}: {
  delivery: ShareEmailDelivery;
  busy: boolean;
  onAction: (delivery: ShareEmailDelivery, action: ShareEmailDeliveryAction) => void;
}) {
  const stateLabel: Record<string, string> = {
    waiting: delivery.readiness.ready ? "Files ready · Queued to send" : "Waiting for files",
    needs_confirmation: "Needs confirmation",
    sending: "Sending",
  };
  return (
    <div className="rounded-lg border border-blue-700/70 bg-slate-950/95 p-3 text-white shadow-xl" data-testid={`share-delivery-${delivery.id}`}>
      <div className="flex items-start gap-2">
        {delivery.state === "sending" ? <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-blue-300" /> : <Clock3 className="mt-0.5 h-4 w-4 text-blue-300" />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{delivery.previewTitle}</p>
          <p className="text-xs text-slate-300">{stateLabel[delivery.state] || delivery.state} · {delivery.projectName}</p>
          {delivery.error && <p className="mt-1 text-xs text-rose-300">{delivery.error}</p>}
        </div>
        <Mail className="h-4 w-4 shrink-0 text-slate-500" />
      </div>
      {delivery.state === "sending" ? (
        <p className="mt-2 text-right text-xs text-slate-400">SMTP submission is in progress; waiting for a confirmed result.</p>
      ) : (
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onAction(delivery, "cancel")} className="h-7 text-xs text-rose-300">
            Cancel
          </Button>
          <Button type="button" size="sm" disabled={busy} onClick={() => onAction(delivery, "force")} className="h-7 bg-amber-600 text-xs hover:bg-amber-500">
            Send now
          </Button>
        </div>
      )}
    </div>
  );
}

function TerminalCard({
  delivery,
  busy,
  onAcknowledge,
}: {
  delivery: ShareEmailDelivery;
  busy: boolean;
  onAcknowledge: () => void;
}) {
  const successful = delivery.state === "sent";
  const uncertain = delivery.state === "uncertain";
  return (
    <div
      className={`rounded-lg border p-3 text-white shadow-xl ${successful ? "border-emerald-600 bg-emerald-950/95" : "border-rose-700 bg-rose-950/95"}`}
      data-testid={`share-delivery-terminal-${delivery.id}`}
    >
      <div className="flex items-start gap-2">
        {successful ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-300" /> : <XCircle className="mt-0.5 h-4 w-4 text-rose-300" />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {successful
              ? `Automatic Send of ${delivery.previewTitle} / ${delivery.projectName} Successful`
              : uncertain
                ? "Email status is uncertain"
                : `Share email ${delivery.state}`}
          </p>
          {successful && delivery.projectThumbnailUrl && (
            <img src={delivery.projectThumbnailUrl} alt="" className="mt-2 h-12 w-20 rounded object-cover" />
          )}
          <p className="mt-1 text-xs text-slate-200">
            {successful
              ? "The SMTP server confirmed delivery."
              : uncertain
                ? "Check your Sent mail before taking any further action. No automatic retry was made."
                : delivery.error || "The durable delivery ended without sending."}
          </p>
          {successful && delivery.error && (
            <p
              className="mt-2 rounded border border-amber-700/70 bg-amber-950/40 p-2 text-xs text-amber-200"
              data-testid={`share-delivery-bookkeeping-error-${delivery.id}`}
            >
              Delivery succeeded, but bookkeeping still needs attention: {delivery.error}
            </p>
          )}
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button type="button" size="sm" disabled={busy} onClick={onAcknowledge} className={successful ? "bg-emerald-700 hover:bg-emerald-600" : "bg-rose-700 hover:bg-rose-600"}>
          OK
        </Button>
      </div>
    </div>
  );
}

export default ShareEmailDeliveryMonitor;